import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const ragArchitectureComponent: ComponentDef = {
  id: 'rag-architecture',
  name: 'RAG Architecture',
  description: 'Retrieval-Augmented Generation: encode query, retrieve documents, augment generator context.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Query length', type: 'number', default: 64, min: 1, max: 8192, step: 1 },
    { name: 'k_docs', label: 'Retrieved docs', type: 'number', default: 3, min: 1, max: 16, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'rag_pipeline',
      options: [
        { label: 'RAG Pipeline', value: 'rag_pipeline' },
        { label: 'Chunking & Retrieval', value: 'chunking' },
        { label: 'Advanced RAG', value: 'advanced' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const k_docs = Number(params.k_docs);
    const variant = String(params.variant || 'rag_pipeline');

    const g = new GraphBuilder();

    if (variant === 'chunking') {
      // Chunking & Retrieval: show document chunking, embedding, indexing, and retrieval
      g.input('query', [seq_len, d_model]);

      // Document chunking: split into overlapping chunks
      g.input('raw_doc', [seq_len * 4, d_model]);
      g.split('chunk_0', 'raw_doc', [seq_len, d_model], 'chunk 0');
      g.split('chunk_1', 'raw_doc', [seq_len, d_model], 'chunk 1');
      g.split('chunk_2', 'raw_doc', [seq_len, d_model], 'chunk 2');

      // Embed each chunk
      g.embedding('chunk_emb_0', 'chunk_0', [1, d_model]);
      g.embedding('chunk_emb_1', 'chunk_1', [1, d_model]);
      g.embedding('chunk_emb_2', 'chunk_2', [1, d_model]);

      // Build index and retrieve
      g.concat('index', ['chunk_emb_0', 'chunk_emb_1', 'chunk_emb_2'], [3, d_model]);
      g.linear('query_enc', 'query', [d_model, d_model]);
      g.matmul('scores', 'query_enc', 'index', [seq_len, 3]);
      g.softmax('top_k', 'scores', -1);

      g.add('augmented', 'query', 'top_k');
      g.output('out', 'augmented');

    } else if (variant === 'advanced') {
      // Advanced RAG: query rewriting, re-ranking, multi-hop retrieval
      g.input('query', [seq_len, d_model]);

      // Query rewriting / expansion
      g.linear('rewriter', 'query', [d_model, d_model]);
      g.activation('rewritten', 'rewriter', 'Query Rewrite');

      // First-hop retrieval
      g.linear('retriever_1', 'rewritten', [d_model, d_model]);
      const docIds1: string[] = [];
      for (let i = 0; i < k_docs; i++) {
        const id = `hop1_doc_${i}`;
        g.embedding(id, 'retriever_1', [1, d_model]);
        docIds1.push(id);
      }
      g.concat('hop1_context', docIds1, [k_docs, d_model]);

      // Re-ranking: score docs against rewritten query
      g.matmul('rerank_scores', 'rewritten', 'hop1_context', [seq_len, k_docs]);
      g.softmax('reranked', 'rerank_scores', -1);

      // Second-hop retrieval using top doc
      g.linear('retriever_2', 'reranked', [d_model, d_model]);
      g.embedding('hop2_doc', 'retriever_2', [1, d_model]);

      // Fuse both hops
      g.concat('multi_hop', ['hop1_context', 'hop2_doc'], [k_docs + 1, d_model]);
      g.add('augmented', 'query', 'multi_hop');
      g.linear('generator', 'augmented', [d_model, d_model]);
      g.output('out', 'generator');

    } else {
      // RAG Pipeline (default): standard encode -> retrieve -> generate
      g.input('query', [seq_len, d_model]);

      g.linear('query_enc', 'query', [d_model, d_model]);
      g.activation('retriever', 'query_enc', 'Retriever');

      const docIds: string[] = [];
      for (let i = 0; i < k_docs; i++) {
        const id = `doc_${i}`;
        g.embedding(id, 'retriever', [1, d_model]);
        docIds.push(id);
      }

      let contextId: string;
      if (docIds.length === 1) {
        contextId = docIds[0];
      } else {
        contextId = 'context';
        g.concat(contextId, docIds, [k_docs, d_model]);
      }

      g.add('augmented', 'query', contextId);
      g.linear('generator', 'augmented', [d_model, d_model]);
      g.output('out', 'generator');
    }

    return g.build();
  }
};
